import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CheckoutSteps from '../Components/CheckoutSteps';
import { firebaseAuth } from '../firebase';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPasswordScreen() {
  const history = useHistory();
  const query = useQuery();
  const [oobCode, setOobCode] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying | ready | submitting | done

  useEffect(() => {
    const code = query.get('oobCode') || '';
    setOobCode(code);
    if (!code) {
      setError('Invalid or missing reset code. Please re-open the link from your email.');
      setStatus('ready');
      return;
    }
    // Verify the code is valid and get the email
    (async () => {
      try {
        const email = await verifyPasswordResetCode(firebaseAuth, code);
        setVerifiedEmail(email || '');
        setStatus('ready');
      } catch (e) {
        setError(e?.message || 'This password reset link is invalid or has expired.');
        setStatus('ready');
      }
    })();
  }, [query]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!oobCode) {
      setError('Missing reset code.');
      return;
    }
    try {
      setStatus('submitting');
      await confirmPasswordReset(firebaseAuth, oobCode, password);
      setStatus('done');
      // Redirect to sign-in with a message
      history.push('/signin');
    } catch (e) {
      setStatus('ready');
      setError(e?.message || 'Failed to reset password. Please try again.');
    }
  };

  return (
    <div>
      {/* Keeping visual consistency with other screens */}
      <CheckoutSteps step1></CheckoutSteps>
      <form className="form" onSubmit={onSubmit}>
        <div>
          <h1>Reset Password</h1>
        </div>
        {verifiedEmail ? (
          <div style={{ marginBottom: 10 }}>
            <small>Resetting password for: <strong>{verifiedEmail}</strong></small>
          </div>
        ) : null}
        {error ? (
          <div className="error" style={{ color: '#b91c1c', marginBottom: 10 }}>{error}</div>
        ) : null}
        {status === 'verifying' ? (
          <div>Verifying link…</div>
        ) : (
          <>
            <div>
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="confirm">Confirm Password</label>
              <input
                id="confirm"
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <div>
              <button className="primary" type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Saving…' : 'Save Password'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
